import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "./api";
import { eventKey, mergeClassroomEvents, normalizeDashboardEvent, readDashboardStorage, reconcileClassroomEvents } from "./dashboardGadgets";
import { flushClassroomDeletions, schoolCalendarForChild, waitForClassroomSync } from "./classroomEventSync";
import { deleteSharedAssignment } from "./assignmentDeletion";

const savedEvents = () => {
    const events = readDashboardStorage("shadowGroveEvents", []);
    return Array.isArray(events) ? events : [];
};
export const classroomEventPayload = (event) => {
    const normalized = normalizeDashboardEvent(event);
    return { ...normalized, data: { ...normalized.data, ...normalized, data: undefined } };
};

export default function useClassroomDashboard({ role, children, subjects }) {
    const childIdsKey = JSON.stringify(children.map((child) => String(child.child_id)).sort());
    const [events, setEvents] = useState(() => role === "parent" ? savedEvents() : []);
    const [sharedSubjects, setSharedSubjects] = useState({});
    const [calendars, setCalendars] = useState({});
    const [layouts, setLayouts] = useState({});
    const [assignmentOrders, setAssignmentOrders] = useState({});
    const [permissions, setPermissions] = useState({});
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(new Set());
    const busyRef = useRef(new Set());
    const revision = useRef(0);
    const authorizedEvents = useRef([]);
    const publish = useCallback((next) => {
        localStorage.setItem("shadowGroveEvents", JSON.stringify(next));
        const scoped = role === "child" ? next.filter((event) => JSON.parse(childIdsKey).includes(String(event.child_id))) : next;
        if (role === "child") authorizedEvents.current = scoped;
        setEvents(scoped);
        window.dispatchEvent(new CustomEvent("shadowGroveEventsUpdated", { detail: next }));
    }, [role, childIdsKey]);

    useEffect(() => {
        let cancelled = false;
        let inFlight = false;
        const refresh = async () => {
            if (inFlight || busyRef.current.size || document.hidden) return;
            inFlight = true;
            const startRevision = revision.current;
            const ids = JSON.parse(childIdsKey);
            const results = await Promise.allSettled(ids.map(async (id) => {
                const url = role === "child" ? "/auth/child/classroom-dashboard" : `/children/${encodeURIComponent(id)}/classroom-dashboard`;
                if (role === "parent") {
                    await waitForClassroomSync();
                    await flushClassroomDeletions([id]);
                }
                let response = await api.get(url);
                if (!Array.isArray(response.data?.events) || !Array.isArray(response.data?.subjects)) throw new Error("Invalid classroom dashboard");
                if (role === "parent" && !cancelled && startRevision === revision.current) {
                    const remoteById = new Map(response.data.events.map((event) => [eventKey(event), event]));
                    const knownSubjects = new Set(response.data.subjects.map((subject) => String(subject.subject_id ?? subject.id)));
                    const reconciled = reconcileClassroomEvents(savedEvents(), response.data.subjects, [...readDashboardStorage("shadowGroveLegacySubjects", []), ...readDashboardStorage("shadowGroveSubjects", [])]);
                    const dirty = reconciled.filter((event) => {
                        const remote = remoteById.get(eventKey(event));
                        return String(event.child_id) === id && knownSubjects.has(String(event.subject_id)) &&
                            (!remote || (Date.parse(event.updated_at || event.recovery_moved_at || "") || 0) > (Date.parse(remote.updated_at || "") || 0));
                    });
                    const sourceCalendar = readDashboardStorage("shadowGroveSchoolCalendar", {});
                    const calendar = schoolCalendarForChild(sourceCalendar, id);
                    const blocks = Object.fromEntries(response.data.subjects.map((subject) => [String(subject.subject_id ?? subject.id), readDashboardStorage(`shadowGroveWeekBlocks_${id}_${subject.subject_id ?? subject.id}`, [])]));
                    const grading_settings = Object.fromEntries(response.data.subjects.map((subject) => [String(subject.subject_id ?? subject.id), readDashboardStorage(`shadowGroveGrading_${subject.subject_id ?? subject.id}`, null)]).filter(([, config]) => config));
                    const time_zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
                    if (dirty.length || JSON.stringify(response.data.calendar || {}) !== JSON.stringify(calendar) || JSON.stringify(response.data.blocks || {}) !== JSON.stringify(blocks) || response.data.time_zone !== time_zone || Object.keys(grading_settings).length) {
                        response = await api.post(`/children/${encodeURIComponent(id)}/classroom-dashboard/sync`, { events: dirty.slice(0, 1000), calendar, blocks, grading_settings, time_zone });
                    }
                }
                return { id, data: response.data };
            }));
            if (!cancelled && startRevision === revision.current) {
                let next = role === "parent" ? savedEvents() : authorizedEvents.current.filter((event) => ids.includes(String(event.child_id)));
                let failed = false;
                results.forEach((result) => {
                    if (result.status !== "fulfilled") { failed = true; return; }
                    const { id, data } = result.value;
                    next = role === "child" ? [...next.filter((event) => String(event.child_id) !== id), ...data.events.map(normalizeDashboardEvent)] : mergeClassroomEvents(reconcileClassroomEvents(next, data.subjects, [...readDashboardStorage("shadowGroveLegacySubjects", []), ...readDashboardStorage("shadowGroveSubjects", [])]), data.events, id);
                    if (role === "parent") {
                        for (const [subjectId, config] of Object.entries(data.grading_settings || {})) {
                            const key = `shadowGroveGrading_${subjectId}`;
                            if (!localStorage.getItem(key)) {
                                localStorage.setItem(key, JSON.stringify(config));
                                window.dispatchEvent(new CustomEvent("shadowGroveGradingSettingsUpdated"));
                            }
                        }
                    }
                    const todos = readDashboardStorage("shadowGroveScheduledTodos", []);
                    localStorage.setItem("shadowGroveScheduledTodos", JSON.stringify([...(Array.isArray(todos) ? todos : []).filter((item) => String(item.child_id) !== id), ...(data.todo_calendar || [])]));
                    setSharedSubjects((current) => ({ ...current, [id]: data.subjects }));
                    setCalendars((current) => ({ ...current, [id]: data.calendar || {} }));
                    setLayouts((current) => ({ ...current, [id]: data.layouts || [] }));
                    setAssignmentOrders((current) => ({ ...current, [id]: data.assignment_orders || [] }));
                    setPermissions((current) => ({ ...current, [id]: data.child_permissions || {} }));
                });
                try {
                    const otherEvents = role === "child" ? savedEvents().filter((event) => !ids.includes(String(event.child_id))) : [];
                    if (role === "child") {
                        authorizedEvents.current = next;
                        setEvents(next);
                        localStorage.setItem("shadowGroveEvents", JSON.stringify([...otherEvents, ...next]));
                        window.dispatchEvent(new CustomEvent("shadowGroveEventsUpdated", { detail: [...otherEvents, ...next] }));
                    } else publish(next);
                } catch { failed = true; }
                setError(failed ? "Some classrooms could not synchronize. Showing saved work; retrying automatically." : "");
            }
            inFlight = false;
            if (!cancelled) setLoading(false);
        };
        const localRefresh = () => {
            if (role === "parent") setEvents(savedEvents());
            else void refresh();
        };
        void refresh();
        const timer = window.setInterval(refresh, 15000);
        window.addEventListener("focus", refresh);
        window.addEventListener("storage", localRefresh);
        window.addEventListener("shadowGroveEventsUpdated", localRefresh);
        document.addEventListener("visibilitychange", refresh);
        return () => {
            cancelled = true;
            window.clearInterval(timer);
            window.removeEventListener("focus", refresh);
            window.removeEventListener("storage", localRefresh);
            window.removeEventListener("shadowGroveEventsUpdated", localRefresh);
            document.removeEventListener("visibilitychange", refresh);
        };
    }, [role, childIdsKey, publish]);

    const mutate = async (event, action, payload) => {
        const key = eventKey(event);
        if (busyRef.current.has(key)) return false;
        busyRef.current.add(key);
        revision.current += 1;
        setBusy(new Set(busyRef.current));
        try {
            const id = encodeURIComponent(event.event_id ?? event.id);
            let response;
            if (role === "parent" && !event.event_id && ["edit", "delete"].includes(action)) {
                await api.post(`/children/${encodeURIComponent(event.child_id)}/classroom-dashboard/sync`, { events: [event] });
            }
            if (action === "create") {
                const saved = await api.post(`/children/${encodeURIComponent(event.child_id)}/classroom-dashboard/sync`, { events: [classroomEventPayload({ ...event, ...payload, updated_at: new Date().toISOString() })] });
                const created = saved.data.events?.find((item) => String(item.event_id ?? item.id) === String(event.event_id ?? event.id));
                if (!created) throw new Error("Assignment could not be created.");
                response = { data: created };
            }
            if (action === "unfinished") response = await api.post(`/children/${encodeURIComponent(event.child_id)}/classroom-dashboard/events/${id}/unfinished`, payload);
            if (action === "progress") response = await api.patch(`/auth/child/classroom-dashboard/events/${id}/progress`, payload);
            if (action === "review") response = await api.post(`/children/${encodeURIComponent(event.child_id)}/classroom-dashboard/events/${id}/review`, payload);
            if (action === "request") response = await api.post(`/auth/child/classroom-dashboard/events/${id}/postponements`, payload);
            if (action === "decision") response = await api.post(`/children/${encodeURIComponent(event.child_id)}/classroom-dashboard/events/${id}/postponements/${encodeURIComponent(payload.request_id)}`, { decision: payload.decision });
            if (action === "edit") response = await api.put(`/classroom-events/${id}`, classroomEventPayload({ ...event, ...payload }));
            if (action === "delete") { publish(await deleteSharedAssignment(event, payload?.scope || "event")); return true; }
            const next = (role === "child" ? authorizedEvents.current : savedEvents()).filter((item) => eventKey(item) !== key);
            if (action !== "delete") next.push(normalizeDashboardEvent(response.data));
            publish(next);
            if (action === "review") window.dispatchEvent(new CustomEvent("shadowGroveMeritsUpdated"));
            return true;
        } catch (failure) {
            toast.error(failure?.response?.data?.detail || "This change could not be saved. Please try again.");
            return false;
        } finally {
            busyRef.current.delete(key);
            setBusy(new Set(busyRef.current));
        }
    };

    const saveOrder = async (date, order) => {
        if (busyRef.current.has("layout")) return false;
        busyRef.current.add("layout");
        revision.current += 1;
        setBusy(new Set(busyRef.current));
        try {
            const response = await api.put("/auth/child/classroom-dashboard/layout", { date, order });
            const id = String(children[0].child_id);
            setLayouts((current) => ({ ...current, [id]: [...(current[id] || []).filter((layout) => layout.date !== date), response.data] }));
            return true;
        } catch {
            toast.error("Your block card arrangement could not be saved.");
            return false;
        } finally {
            busyRef.current.delete("layout");
            setBusy(new Set(busyRef.current));
        }
    };
    const saveAssignmentOrder = async (card, date, order) => {
        const key = `order:${card.id}`;
        if (busyRef.current.has(key)) return false;
        busyRef.current.add(key); setBusy(new Set(busyRef.current)); revision.current += 1;
        try {
            const url = role === "child" ? "/auth/child/classroom-dashboard/assignment-order" : `/children/${encodeURIComponent(card.child.child_id)}/classroom-dashboard/assignment-order`;
            const response = await api.put(url, { date, subject_id: card.subjectId, order });
            const childId = String(card.child.child_id);
            setAssignmentOrders((current) => ({ ...current, [childId]: [...(current[childId] || []).filter((row) => row.date !== date || row.subject_id !== card.subjectId), response.data] }));
            if (Array.isArray(response.data.events)) publish(role === "child" ? response.data.events : mergeClassroomEvents(savedEvents(), response.data.events, childId));
            return true;
        } catch { toast.error("Assignment order could not be saved."); return false; }
        finally { busyRef.current.delete(key); setBusy(new Set(busyRef.current)); }
    };
    const createAssignments = async (items) => {
        if (!items.length || busyRef.current.has("batch")) return false;
        busyRef.current.add("batch"); setBusy(new Set(busyRef.current)); revision.current += 1;
        try {
            const childId = items[0].child_id;
            if (items.some((item) => item.child_id !== childId || item.subject_id !== items[0].subject_id)) throw new Error("Choose one classroom for a series.");
            const response = await api.post(`/children/${encodeURIComponent(childId)}/classroom-dashboard/sync`, { events: items.map(classroomEventPayload) });
            if (!items.every((item) => response.data.events?.some((saved) => String(saved.event_id ?? saved.id) === String(item.id)))) throw new Error("Some assignments could not be saved. Please retry.");
            publish(mergeClassroomEvents(savedEvents(), response.data.events, String(childId)));
            return true;
        } catch (error) { toast.error(error?.response?.data?.detail || error.message || "Assignments could not be saved."); return false; }
        finally { busyRef.current.delete("batch"); setBusy(new Set(busyRef.current)); }
    };
    const ids = JSON.parse(childIdsKey);
    const resolvedSubjects = ids.flatMap((id) => sharedSubjects[id] || subjects.filter((subject) => String(subject.child_id) === id));
    return { events, subjects: resolvedSubjects, calendars, layouts, assignmentOrders, permissions, busy, mutate, createAssignments, saveAssignmentOrder, saveOrder, error, loading };
}
